import { FeathersService, Application, Service } from "@feathersjs/feathers/lib";
import { action, computed, makeObservable, observable } from "mobx";
import { rootStore } from "src/app";
import client from "src/feathers";
import { CommentReaction, IComment, ICommentReaction, IItem, ItemType, Services } from "../types";
import { Data, Item, Question } from "./";
import { DataType, } from "./../types";

export class Comment extends Data<IComment> implements IComment {
  public static readonly mentionIdentifier: string = "@";
  public static readonly mentionFiller:     string = "\u0001";
  protected static readonly reaction_service: FeathersService<Application<ICommentReaction>, Service<ICommentReaction>> = client.service(Services.Reaction);
  down_votes?: number   = 0;
  item_id:     number   = null;
  meeting_id?: number   = null;
  private?:    boolean  = null;
  reply_id?:   number   = null;
  text:        string   = null;
  data_type:   ItemType = null;
  up_votes?:   number   = 0;
  user_id:     number   = null;
  room_id:     number   = null;
  replies:     number[] = [];
  mentions?:   number[] = [];
  user_reactions?: CommentReaction[] = [];
  protected constructor(data: IComment) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Comment, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      down_votes: observable,
      meeting_id: observable,
      private:    observable,
      reply_id:   observable,
      text:       observable,
      data_type:  observable,
      up_votes:   observable,
      user_id:    observable,
      replies:    observable,
      mentions:   observable,
      room_id:    observable,

      getNumReplies: computed,
      getReplies:    computed,
      getItem:       computed,
      isAnswer:      computed,
      getText:       computed,

      updateReaction: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  public static create(data?: IComment): Comment {
    const d = {
      ...data,
      down_votes:     data.down_votes || 0,
      item_id:        data.item_id,
      meeting_id:     data.meeting_id || null,
      private:        data.private || null,
      reply_id:       data.reply_id || null,
      text:           data.text,
      up_votes:       data.up_votes || 0,
      user_id:        data.user_id,
      user_reactions: data.user_reactions,
      data_type:      data.data_type,
      room_id:        data.room_id,
      mentions:       data.mentions || [],
      ...Data.create(data),
    };
    return new Comment(d);
  }

  protected fromData(data: IComment): void {
    this.down_votes = Number(data.down_votes || 0);
    this.item_id    = Number(data.item_id);
    this.room_id    = Number(data.room_id);
    this.meeting_id = data.meeting_id && Number(data.meeting_id);
    this.private    = data.private;
    this.reply_id   = data.reply_id && Number(data.reply_id);
    this.text       = data.text;
    this.up_votes   = Number(data.up_votes ||0);
    this.user_id    = data.user_id;
    this.data_type  = data.data_type,
    this.user_reactions = data.user_reactions;
    this.replies    = data.replies ? data.replies.map(r => Number(r)).filter(r => Boolean(r)) : [];
    this.mentions   = data.mentions ? data.mentions.map(r => Number(r)).filter(r => Boolean(r)) : [];
  }

  public toData(): any {
    return {
      ...super.toData(),
      item_id:    this.item_id,
      meeting_id: this.meeting_id,
      private:    this.private,
      reply_id:   this.reply_id,
      text:       this.text,
      user_id:    this.user_id,
      data_type:  this.data_type,
      room_id:    this.room_id,
      mentions:   this.mentions,
    };
  }

  protected toPatchData(patch: Partial<IComment>): any {
    return {
      ...super.toPatchData(patch),
      item_id:    "item_id" in patch ? patch.item_id :       undefined,
      meeting_id: "meeting_id" in patch ? patch.meeting_id : undefined,
      private:    "private" in patch ? patch.private :       undefined,
      reply_id:   "reply_id" in patch ? patch.reply_id :     undefined,
      text:       "text" in patch ? patch.text :             undefined,
      user_id:    "user_id" in patch ? patch.user_id :       undefined,
      room_id:    "room_id" in patch ? patch.room_id :       undefined,
      data_type:  "data_type" in patch ? patch.data_type :   undefined,
      mentions:   "mentions" in patch ? patch.mentions :     undefined,
    };
  }

  public async react(reaction: CommentReaction): Promise<void> {
    await Comment.reaction_service.create({ 
      comment_id:   this.id,
      item_id:      this.item_id,
      reaction:     reaction,
      room_id:      this.room_id,
      data_type:    this.data_type,
      workspace_id: this.workspace_id,
    });
  }

  public get getVote(): string {
    const vote = this.up_votes - this.down_votes;
    return vote > 0 ? `+${vote}` : `${vote}`
  }

  public updateReaction = (reaction: ICommentReaction, add: boolean): void => {
    switch(reaction.reaction) {
      case CommentReaction.Up: 
        this.up_votes = this.up_votes + (add ? 1 : -1);
        break;
      case CommentReaction.Down:
        this.down_votes = this.down_votes + (add ? 1 : -1);
        break;
      default: 
        break;
    }
  }

  get getNumReplies(): number {
    return this.replies.length;
  }

  get getReplies(): Comment[] {
    return this.replies.map(r => rootStore.commentStore.container[r]).filter(r => Boolean(r));
  }

  get getItem(): Item<IItem> {
    return rootStore.getActiveItem;
  }

  get isAnswer(): boolean {
    return (rootStore.getActiveItem as Question)["answered"] === this.id;
  }

  get getText(): string {
    if(!this.mentions || !this.mentions.length) return this.text;
    const mentionStrings: string[] = this.mentions.map(id => Comment.mentionIdentifier + rootStore.userStore.container[id]?.getFullName || "@invalid reference");
    return this.text.split(Comment.mentionFiller).reduce((res, val, idx) => res + val + (mentionStrings[idx] || ""), "");
  }

  /**
   * Inserts @user_id at the right position of @mentions given @text .
   * In a comment, the list of mentions is assumed to be in order of occurance. 
   */
  public static sortMention = (mentions: number[], text: string): number[] => {
    const mentionStrings: string[] = mentions.map(id => Comment.mentionIdentifier + rootStore.userStore.container[id]?.getFullName || "@invalid reference");
    const _t:      string   = text;
    const indices: number[] = []; // Hold the indices in the string of the mentions.
    let   removed: number   = 0;

    // To get the correct string indices when a mention occurs multiple times, 
    // find the next occurence, then remove it from the string and remember
    // how many chars were removed
    mentionStrings.forEach(m => {
      indices.push(_t.indexOf(m) + removed);
      removed += m.length;
      _t.replace(m, "");
    });

    // Sort @mentions according to the order of @indices
    const out: number[] = mentions.map((m, idx) => ({m, idx})).sort((lhs, rhs) => lhs.idx - rhs.idx).map(m => m.m);

    return out;
  }
}
